import styled from 'styled-components';
import { SxProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker as DateTimePickerBase } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { Flex } from '../Flex';
import { ComponentSize, COMPONENT_SIZE_MAP } from '../../types/types';

const Wrapper = styled(Flex)`
  gap: 8px;
  align-items: center;
`;

const DateTimePicker = styled(DateTimePickerBase)<{ $size?: ComponentSize }>`
  .MuiInputBase-root {
    height: ${({ $size }) => COMPONENT_SIZE_MAP[$size || 'medium']}px;
    font-size: 14px;
    min-height: ${({ $size }) => COMPONENT_SIZE_MAP[$size || 'medium']}px;
  }

  & .MuiInputLabel-root {
    top: -6px;
  }

  & .MuiInputLabel-shrink {
    top: 0;
  }

  .MuiIconButton-root {
    height: 32px;
    width: 32px;
  }

  .MuiSvgIcon-root {
    height: 20px;
    width: 20px;
  }
`;

export type DateTimeRangeValue = {
  start: dayjs.Dayjs | null;
  end: dayjs.Dayjs | null;
};

type Props = {
  value?: DateTimeRangeValue;
  onChange: (newValue: DateTimeRangeValue) => void;
  format?: string;
  disableFuture?: boolean;
  disabled?: boolean;
  fromLabel?: string;
  toLabel?: string;
  maxDateTime?: dayjs.Dayjs;
  sx?: SxProps;
  size?: ComponentSize;
};

const DateTimeRangePicker = ({
  value,
  onChange,
  format = 'YYYY-MM-DD HH:mm',
  fromLabel = 'Start',
  toLabel = 'End',
  disableFuture = false,
  disabled = false,
  maxDateTime,
  sx,
  size = 'medium',
}: Props) => (
  <Wrapper sx={sx}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        $size={size}
        label={fromLabel}
        value={value?.start}
        disabled={disabled}
        onChange={(newValue) => {
          onChange({
            start: newValue,
            end: newValue?.isAfter(value?.end) ? null : value?.end || null,
          });
        }}
        maxDateTime={value?.end || undefined}
        disableFuture={disableFuture}
        format={format}
        timeSteps={{ hours: 1 }}
        slotProps={{
          textField: { size: 'small' },
          digitalClockSectionItem: {
            sx: { fontSize: '14px' },
          },
        }}
        views={['year', 'month', 'day', 'hours']}
      />
      -
      <DateTimePicker
        $size={size}
        label={toLabel}
        value={value?.end}
        disabled={disabled}
        onChange={(newValue) => {
          onChange({
            start: newValue?.isBefore(value?.start)
              ? null
              : value?.start || null,
            end: newValue,
          });
        }}
        minDateTime={value?.start || undefined}
        maxDateTime={maxDateTime}
        format={format}
        disableFuture={disableFuture}
        timeSteps={{ hours: 1 }}
        slotProps={{
          textField: { size: 'small' },
          digitalClockSectionItem: {
            sx: { fontSize: '14px' },
          },
        }}
        views={['year', 'month', 'day', 'hours']}
      />
    </LocalizationProvider>
  </Wrapper>
);

export default DateTimeRangePicker;
